/**
 * formatBytes — one localised file-size formatter for every member surface
 * (055, owner decision 2026-09-30).
 *
 * Extracted from the compose wizard, which already formatted sizes with
 * language-appropriate units, so that the conversation's attachment chips stop
 * printing "180 kB" in Finnish where the convention is "180 kt". Two formatters
 * with different unit handling is exactly the kind of split that drifts apart
 * silently (Constitution P VIII: one authoritative representation).
 *
 * The UNITS are passed in rather than looked up here: this module stays free of
 * the i18n hook so it can be unit-tested directly and used from a non-component
 * context. The caller supplies them from
 * `compose.wizard.attachments.unit.{bytes,kilobytes,megabytes}` — the keys that
 * already exist in all three locales (fi: t/kt/Mt, en: B/KB/MB, sv: B/kB/MB).
 * The NUMBER is formatted for the member's locale too (`localeTag`): Finnish and
 * Swedish use a decimal comma, and a whole value prints without a forced `.0`
 * ("180 kt", "1,5 Mt").
 *
 * No gigabyte arm: the member attachment ceiling is 25 MB (DL-027), so anything
 * that would need one has already been refused.
 */

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { localeTag } from '../i18n/localeTag.js';

export interface ByteUnits {
  readonly b: string;
  readonly kb: string;
  readonly mb: string;
}

/**
 * Format a byte count for display, in the member's language.
 *
 * Returns `null` for an unknown or nonsensical size (the Integration Service
 * reports `size` as nullable) so callers can omit the element entirely rather
 * than print a placeholder — an attachment whose size we were not told is shown
 * without one, never as "0 B" (P VII: no fabricated value).
 */
export function formatBytes(
  sizeBytes: number | null | undefined,
  active: LanguageCode,
  units: ByteUnits,
): string | null {
  if (sizeBytes === null || sizeBytes === undefined || sizeBytes < 0) {
    return null;
  }
  const number = new Intl.NumberFormat(localeTag(active), { maximumFractionDigits: 1 });
  if (sizeBytes < 1024) {
    return `${number.format(sizeBytes)} ${units.b}`;
  }
  // Pick the unit AFTER rounding, so 1023.96 KiB reads "1 Mt", never "1 024 kt".
  const kb = Math.round((sizeBytes / 1024) * 10) / 10;
  if (kb < 1024) {
    return `${number.format(kb)} ${units.kb}`;
  }
  return `${number.format(sizeBytes / (1024 * 1024))} ${units.mb}`;
}
